<template>
  <v-card class="mt-5 mx-auto">
    <v-card-title class="primary--text">
      <v-icon color="primary" left>mdi-account</v-icon>
      Edit User: {{ user.username.split("@")[0] }}
      <v-spacer></v-spacer>
      {{ user.email }} <v-btn class="ml-5" @click="closeEditUser">Close</v-btn>
    </v-card-title>
    <v-card-text>
      <v-select
        v-model="assignedRoles"
        :items="allRoles"
        label="Select Roles"
        multiple
        chips
        deletable-chips
      >
      </v-select>

      <v-select
        v-model="assignedSites"
        :items="allSites"
        item-text="name"
        return-object
        label="Select Site(s)"
        multiple
        chips
        deletable-chips
      >
      </v-select>

      <v-select
        v-model="assignedVessels"
        :items="allVessels"
        item-text="name"
        return-object
        label="Select Vessel(s)"
        multiple
        chips
        deletable-chips
      >
      </v-select>

      <v-select
        v-model="assignedNotifications"
        :items="assignableNotifications"
        item-text="siteAndValueString"
        item-value="key"
        return-object
        label="Select Notifications(s)"
        multiple
        chips
        deletable-chips
      >
      </v-select>

      <v-form ref="form" v-model="validNewPassword" lazy-validation>
        <v-text-field
          :append-icon="showNewPwd ? 'mdi-eye' : 'mdi-eye-off'"
          :rules="[rules.required, rules.password]"
          :type="showNewPwd ? 'text' : 'password'"
          name="input-10-2"
          label="New password"
          hint="Min. 8 characters,one capital letter, a number and a special character."
          v-model="newPassword"
          class="input-group--focused"
          @click:append="showNewPwd = !showNewPwd"
        >
          <template v-slot:append-outer>
            <v-btn
              :disabled="!resetPasswordEnabled"
              color="primary"
              class="mr-4"
              @click="resetUserPassword"
              >Reset Password</v-btn
            >
          </template>
        </v-text-field>
      </v-form>

      <v-switch
        v-model="userAccountDisabled"
        color="red"
        :prepend-icon="accountDisabledIcon"
        :loading="isAccountDisableActionLoading"
        :disabled="isAccountDisableActionLoading"
      >
      </v-switch>
    </v-card-text>
  </v-card>
</template>


<script>
import { mapState, mapGetters } from 'vuex';
import userRoleTagType from '@/mixins/UserRoleTagType';
// import { siteTagType } from '../mixins/SiteTagType'

const hasLowerCase = (str) => (/[a-z]/.test(str));
const hasUpperCase = (str) => (/[A-Z]/.test(str));
const hasNumber = (str) => (/\d/.test(str));
const hasNonAlphaNumeric = (str) => (/[^a-zA-Z0-9]/.test(str));
const isPasswordValid = (password) => hasNonAlphaNumeric(password)
      && hasNumber(password)
      && hasUpperCase(password)
      && hasLowerCase(password)
      && (password.length >= 6);

export default {
  name: 'UserEditForm',
  props: ['user'],
  mixins: [userRoleTagType],
  components: {
  },
  data() {
    return {
      validNewPassword: false,
      deleteUserEnabled: true,
      isLoading: false,
      isFullPageLoading: true,
      isAccountDisableActionLoading: false,
      showModal: false,
      showConfirmDeleteModal: false,
      newPassword: '',
      failedToResetUserPasswordMessage: '',
      isFailMessageActive: false,
      assignedRoles: [],
      assignedSites: [],
      assignedVessels: [],
      assignedNotifications: [],
      showNewPwd: true,
      rules: {
        required: (value) => !!value || 'Required.',
        password: (value) => {
          const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/; // eslint-disable-line no-useless-escape
          return (
            pattern.test(value)
            || 'Min. 8 characters with at least one capital letter, a number and a special character.'
          );
        },
      },
    };
  },
  computed: {
    ...mapState(['usersAdminStore', 'userStore']),
    ...mapGetters({
      isClientConnectedToServer: 'userStore/isClientConnectedToServer',
      getActiveSite: 'userStore/getActiveSite',
    }),
    allRoles() {
      const { allRoles } = this.usersAdminStore;
      return allRoles;
    },
    allSites() {
      const { allSites } = this.usersAdminStore;
      return allSites;
    },
    allVessels() {
      const { allVessels } = this.usersAdminStore;
      return allVessels;
    },
    allNotifications() {
      const { allNotifications } = this.usersAdminStore;
      return allNotifications;
    },
    assignableRoles() {
      const selectedUserRoles = this.user ? this.user.roles : [];
      const { allRoles } = this.usersAdminStore;
      const rolesThatCanBeAssignedToUser = allRoles.filter((x) => !selectedUserRoles.includes(x));
      return rolesThatCanBeAssignedToUser;
    },
    assignableSites() {
      let assignedSites = this.user ? this.user.assignedSites : [];
      assignedSites = assignedSites || [];
      const assignedSiteIdentifiers = assignedSites.map((x) => x.id);
      const { allSites } = this.usersAdminStore;
      const siteIsNotAssigned = (x) => !(assignedSiteIdentifiers.includes(x.id));
      const sitesThatCanBeAssignedToUser = allSites.filter(siteIsNotAssigned);
      return sitesThatCanBeAssignedToUser;
    },
    assignableNotifications() {
      const { allNotifications } = this;
      const assignedSites = this.user ? this.user.assignedSites : [];
      let result = [];
      assignedSites.forEach((site) => {
        const notificationsForSite = allNotifications.map((x) => ({
          ...x,
          siteId: site.id,
          siteAndValueString: `${site.id} - ${x.description}`,
          key: `${site.id}_${x.name}`,
        }));
        result = [...result, ...notificationsForSite];
      });
      return result;
    },
    userAccountDisabled: {
      get() {
        return this.user.userAccountDisabled;
      },
      set(bool) {
        this.user.userAccountDisabled = bool;
      },
    },
    accountDisabledIcon() {
      if (this.userAccountDisabled) return 'mdi-lock';
      return 'mdi-lock-open';
    },
    resetPasswordEnabled() {
      return !this.isFailMessageActive
        && !this.isLoading
        && this.validNewPassword
        && this.newPassword.length > 1;
    },
    activeSiteId() {
      const site = this.getActiveSite;
      if (!site) {
        return null;
      }
      return site.id;
    },
  },
  watch: {
    assignedRoles: {
      immediate: false,
      handler(val, prev) {
        const rolesAdded = (val || []).filter((x) => !this.user.roles.includes(x));
        rolesAdded.forEach((role) => this.assignRoleToUser(this.user.id, role));

        const rolesRemoved = (prev || []).filter((x) => !(val || []).includes(x));
        const rolesActuallyRemovedFromCurrentlySelectedUser = rolesRemoved
          .filter((x) => this.user.roles.includes(x));
        rolesActuallyRemovedFromCurrentlySelectedUser
          .forEach((role) => this.removeRoleFromUser(this.user.id, role));
      },
    },
    assignedSites: {
      immediate: false,
      handler(val, prev) {
        const sitesAdded = (val || [])
          .filter((x) => !(this.user.assignedSites || []).map((y) => y.name).includes(x.name));
        sitesAdded
          .forEach((site) => this.assignSiteToUser(this.user.id, site));

        const sitesRemoved = (this.user.assignedSites || [])
          .filter((x) => !val.map((y) => y.name).includes(x.name));
        sitesRemoved.forEach((site) => this.removeSiteFromUser(this.user.id, site));
      },
    },
    assignedVessels: {
      immediate: false,
      handler(val, prev) {
        const vesselsAdded = (val || [])
          .filter((x) => !(this.user.assignedVessels || []).map((y) => y.name).includes(x.name));
        vesselsAdded.forEach((vessel) => this.assignVesselToUser(this.user.id, vessel));

        const vesselsRemoved = (this.user.assignedVessels || [])
          .filter((x) => !(val || []).map((y) => y.name).includes(x.name));
        vesselsRemoved.forEach((vessel) => this.removeVesselFromUser(this.user.id, vessel));
      },
    },
    assignedNotifications: {
      immediate: false,
      handler(val, prev) {
        const notificationsAdded = (val || [])
          .filter((x) => !(this.user.assignedNotifications || [])
            .map((y) => y.key)
            .includes(x.key));
        notificationsAdded
          .forEach((notification) => this.assignNotificationToUser(this.user.id, notification));

        const notificationsRemoved = (this.user.assignedNotifications || [])
          .filter((x) => !(val || [])
            .map((y) => y.key)
            .includes(x.key));
        notificationsRemoved
          .forEach((notification) => this.removeNotificationFromUser(this.user.id, notification));
      },
    },
    user: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal.username !== oldVal?.username) {
          this.assignedRoles = newVal.roles; // Change to match new user.
          this.assignedSites = newVal.assignedSites;
          this.assignedVessels = newVal.assignedVessels;
          this.assignedNotifications = newVal.assignedNotifications;
        }
      },
    },
    userAccountDisabled: {
      immediate: false,
      handler(newVal, oldVal) {
        this.disableUserAccountUpdate(this.user.id, newVal);
      },
    },
  },
  methods: {
    onSuccess(message, data) {
      this.$toast.success(message);
    },
    onError(message, data) {
      this.$toast.error(message);
    },
    clearLoadingIndicator() {
      this.isLoading = false;
    },
    deleteUser() {
      this.showConfirmDeleteModal = true;
    },
    deleteUserConfirmed() {
      this.deleteUserEnabled = false;
      this.isLoading = true;
      this.$toast('Deleting user...');
      this.$store.dispatch('usersAdminStore/deleteUser', { userId: this.user.id })
        .then((resp) => { this.onSuccess('User Deleted', resp); })
        .catch((err) => { this.onError('Failed to delete user', err); this.deleteUserEnabled = true; })
        .finally(() => { this.clearLoadingIndicator(); });
    },
    resetUserPassword() {
      this.$toast('Resetting user password...');
      this.$store.dispatch('usersAdminStore/resetUserPassword', { userId: this.user.id, newPassword: this.newPassword })
        .then((resp) => { this.onSuccess('Password has been reset.', resp); })
        .catch((err) => {
          this.onError('Failed to reset user');
          this.failedToResetUserPasswordMessage = `${err.error} : ${err.details}`;
          this.isFailMessageActive = true;
        })
        .finally(() => { this.showModal = false; });
    },
    closeEditUser() {
      this.usersAdminStore.selectedUser = null;
    },
    assignRoleToUser(userId, roleToAssign) {
      this.$toast(`Assigning ${roleToAssign} to user.`);
      this.$store.dispatch('usersAdminStore/assignRoleToUser', { userId, role: roleToAssign })
        .then((resp) => { this.onSuccess('Role assigned', resp); })
        .then(() => { this.$store.dispatch('userStore/reloadUserData', this.activeSiteId); })
        .catch((err) => { this.onError('Failed to assign role', err); });
    },
    removeRoleFromUser(userId, roleToRemove) {
      this.$toast(`Removing ${roleToRemove} from user.`);
      this.$store.dispatch('usersAdminStore/removeRoleFromUser', { userId, role: roleToRemove })
        .then((resp) => { this.onSuccess('Role removed', resp); })
        .then(() => { this.$store.dispatch('userStore/reloadUserData', this.activeSiteId); })
        .catch((err) => { this.onError('Failed to remove role', err); });
    },
    assignSiteToUser(userId, siteToAssign) {
      this.$toast(`Assigning ${siteToAssign.name} to user.`);
      this.$store.dispatch('usersAdminStore/assignSiteToUser', { userId, site: siteToAssign.name })
        .then((resp) => { this.onSuccess('Site assigned', resp); })
        .then(() => { this.$store.dispatch('userStore/reloadUserData', this.activeSiteId); })
        .catch((err) => { this.onError('Failed to assign site', err); });
    },
    removeSiteFromUser(userId, siteToRemove) {
      this.$toast(`Removing ${siteToRemove.name} from user.`);
      this.$store.dispatch('usersAdminStore/removeSiteFromUser', { userId, site: siteToRemove.name })
        .then((resp) => { this.onSuccess('Site removed', resp); })
        .then(() => { this.$store.dispatch('userStore/reloadUserData', this.activeSiteId); })
        .catch((err) => { this.onError('Failed to remove site', err); });
    },
    assignVesselToUser(userId, vesselToAssign) {
      this.$toast(`Assigning ${vesselToAssign.name} to user.`);
      this.$store.dispatch('usersAdminStore/assignVesselToUser', { userId, vessel: vesselToAssign })
        .then((resp) => { this.onSuccess('Vessel assigned', resp); })
        .then(() => { this.$store.dispatch('userStore/reloadUserData', this.activeSiteId); })
        .catch((err) => { this.onError('Failed to assign site', err); });
    },
    removeVesselFromUser(userId, vesselToRemove) {
      if (vesselToRemove) {
        this.$toast(`Removing ${vesselToRemove.name} from user.`);
        this.$store.dispatch('usersAdminStore/removeVesselFromUser', { userId, vessel: vesselToRemove })
          .then((resp) => { this.onSuccess('Vessel removed', resp); })
          .then(() => { this.$store.dispatch('userStore/reloadUserData', this.activeSiteId); })
          .catch((err) => { this.onError('Failed to remove vessel', err); });
      }
    },
    assignNotificationToUser(userId, notificationToAssign) {
      const assignNotificationModel = {
        userId,
        notificationType: notificationToAssign.value,
        siteId: notificationToAssign.siteId,
      };
      this.$toast(`Assigning ${notificationToAssign.name} to user.`);
      this.$store.dispatch('usersAdminStore/assignNotificationToUser', assignNotificationModel)
        .then((resp) => { this.onSuccess('Notification assigned', resp); })
        .then(() => { this.$store.dispatch('userStore/reloadUserData', this.activeSiteId); })
        .catch((err) => { this.onError('Failed to assign notification', err); });
    },
    removeNotificationFromUser(userId, notificationToRemove) {
      if (notificationToRemove) {
        const removeNotificationModel = {
          userId,
          notificationType: notificationToRemove.value,
          siteId: notificationToRemove.siteId,
        };
        this.$toast('Removing Notification from user.');
        this.$store.dispatch('usersAdminStore/removeNotificationFromUser', removeNotificationModel)
          .then((resp) => { this.onSuccess('Notification removed', resp); })
          .then(() => { this.$store.dispatch('userStore/reloadUserData', this.activeSiteId); })
          .catch((err) => { this.onError('Failed to remove notification', err); });
      }
    },
    disableUserAccountUpdate(userId, disabledBool) {
      this.isAccountDisableActionLoading = true;
      this.$store.dispatch('usersAdminStore/disableUserUpdate', { userId, disabledBool })
        .then((resp) => { this.onSuccess('User Account Disabled', resp); })
        .then(() => { this.$store.dispatch('userStore/reloadUserData', this.activeSiteId); })
        .catch((err) => { this.onError('Failed to disable user account', err); })
        .finally(() => { this.isAccountDisableActionLoading = false; });
    },
  },
};
</script>
